import React, { useState } from "react";
import api from "@/lib/api";
import { PageTitle } from "@/components/DashUI";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Sparkles, Copy } from "lucide-react";

const TASKS = [
  ["outreach_email", "Outreach email (family/consumer)"],
  ["referral_email", "Referral-source email"],
  ["provider_email", "Provider email"],
  ["follow_up", "Follow-up message"],
  ["phone_script", "Telephone script"],
  ["follow_up_sequence", "Follow-up sequence"],
  ["provider_intro", "Provider introduction"],
  ["family_explanation", "Family-friendly explanation"],
  ["summarize_notes", "Summarize CRM notes"],
  ["next_action", "Suggest next action"],
  ["prospecting_query", "Prospecting query"],
  ["task_list", "Daily task list"],
];

export default function Scripts() {
  const [task, setTask] = useState("outreach_email");
  const [context, setContext] = useState("");
  const [tone, setTone] = useState("professional");
  const [out, setOut] = useState("");
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true); setOut("");
    try { const { data } = await api.post("/agent/ai/generate", { task, context, tone }); setOut(data.output); }
    catch { toast.error("Generation failed"); }
    finally { setLoading(false); }
  };
  const copy = () => { navigator.clipboard.writeText(out); toast.success("Copied"); };

  return (
    <div>
      <PageTitle title="Scripts & Templates" subtitle="AI-generated outreach, scripts, and follow-ups" />
      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="p-6">
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">What do you need?</Label>
              <Select value={task} onValueChange={setTask}>
                <SelectTrigger data-testid="script-task" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{TASKS.map(([id, label]) => <SelectItem key={id} value={id}>{label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Tone</Label>
              <Select value={tone} onValueChange={setTone}>
                <SelectTrigger data-testid="script-tone" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{["professional", "warm", "concise", "consultative"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Context / details</Label>
              <Textarea data-testid="script-context" className="mt-1 min-h-32" value={context} onChange={(e) => setContext(e.target.value)}
                placeholder="e.g. Discharge planner at Wake Med, private-duty home care, follow up after intro call." />
            </div>
            <Button data-testid="script-generate-btn" onClick={run} disabled={loading} className="w-full bg-clinical hover:bg-clinical/90">
              <Sparkles className="mr-2 h-4 w-4" /> {loading ? "Generating…" : "Generate"}
            </Button>
          </div>
        </Card>
        <Card className="flex flex-col p-6">
          <div className="flex items-center justify-between">
            <h3 className="font-heading font-semibold text-navy">Output</h3>
            {out && <Button size="sm" variant="ghost" onClick={copy}><Copy className="mr-1.5 h-4 w-4" /> Copy</Button>}
          </div>
          <div className="mt-3 flex-1 whitespace-pre-wrap rounded-lg bg-slate-50 p-4 text-sm text-slate-700" data-testid="script-output">
            {out || <span className="text-slate-400">Your generated content will appear here.</span>}
          </div>
        </Card>
      </div>
    </div>
  );
}
